// src/oura/components/client/GbpProfileCard.tsx
//
// The client's live Google Business Profile, recorded by staff, one block per
// outlet (#4010, ourteam-backend#5277).
//
// Oura has no Business Profile API (the owner's decision, 7 Oct 2026), so the
// GBP Pack skill cannot read the profile. This is where it comes from: staff
// type what the profile says now and upload a "before" screenshot per
// section, and the skill reads that instead of someone opening Chrome.
//
// STAFF ONLY, as the routes are. The card is not drawn for a merchant and not
// drawn at all where the flag is off.
//
// The form is built from the payload's own `fieldList`, never from a list kept
// here: a field the backend adds appears without a frontend change, and one it
// drops stops being asked for. Each write answers the whole payload, so every
// block re-renders from the server's answer rather than from what it sent.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ImageIcon, Loader2Icon, MapPinIcon, TrashIcon, UploadIcon } from "lucide-react";
import {
    addGbpScreenshot,
    getGbpProfiles,
    GBP_CAPTION_MAX,
    GBP_MAX_SHOTS,
    GBP_SHOT_MAX_BYTES,
    GBP_SHOT_TYPES,
    readGbpFromGoogle,
    removeGbpScreenshot,
    saveGbpFields,
    type GbpField,
    type GbpOutlet,
    type GbpProfiles,
    type GbpResult,
    type GbpShot,
} from "@service/aeo/gbpProfile";
import { useSnackbar } from "@hook/use-snackbar";
import { useWritesPaused } from "@context/maintenance";
import { Field, Refusal } from "../onboarding/fields";
import { inputClass, textareaClass } from "../../onboarding/messageText";
import {
    BOOL_NO,
    BOOL_YES,
    checkDraft,
    counterText,
    draftChanged,
    draftFrom,
    fieldErrorKey,
    fieldsFrom,
    recordedCount,
    shotLabelText,
    type GbpDraft,
} from "../../gbp/model";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { FoldCard, FOCUS_RING, GroupFold, PillButton, SURFACE, TYPE } from "@oura/ui";

type Load =
    | { kind: "loading" }
    | { kind: "failed"; message: string }
    | { kind: "ready"; data: GbpProfiles };

export function GbpProfileCard({ businessId }: { businessId: string }) {
    const [load, setLoad] = useState<Load>({ kind: "loading" });
    /** Only the newest read may paint: a business switched away from must not land on the next one's card. */
    const seq = useRef(0);

    const read = useCallback(async () => {
        const mine = ++seq.current;
        const r = await getGbpProfiles(businessId);
        if (mine !== seq.current) return;
        setLoad(r.ok ? { kind: "ready", data: r.data } : { kind: "failed", message: r.message });
    }, [businessId]);

    useEffect(() => {
        setLoad({ kind: "loading" });
        void read();
        return () => { seq.current += 1; };
    }, [read]);

    // Every write answers the whole payload, so a block hands it straight back
    // up: no second read, and no screen state that disagrees with the server.
    const apply = useCallback((data: GbpProfiles) => {
        seq.current += 1;
        setLoad({ kind: "ready", data });
    }, []);

    const outlets = load.kind === "ready" ? load.data.outlets : [];
    const recorded = load.kind === "ready"
        ? outlets.filter((o) => Object.keys(o.fields).length > 0 || o.screenshots.length > 0).length
        : 0;

    const snackbar = useSnackbar();
    /** The business on screen right now, readable from a call that started on an earlier render. */
    const onScreen = useRef(businessId);
    onScreen.current = businessId;
    /**
     * Which fill may still change this card's state. Bumped when the business
     * changes and when a fill starts: an older fill that answers late must not
     * clear the busy state of the one that is running now (Jack, #4200).
     */
    const activeFill = useRef(0);

    // WHICH FORMS HAVE UNSAVED EDITS, for the whole card. A Google fill answers
    // the complete payload, so it must not start while ANY outlet is dirty, and a
    // confirmation already open closes the moment one becomes dirty (Jack, #4196).
    const [dirtyByOutlet, setDirtyByOutlet] = useState<Record<string, boolean>>({});
    const reportDirty = useCallback((slug: string, dirty: boolean) => {
        setDirtyByOutlet((was) => (was[slug] === dirty ? was : { ...was, [slug]: dirty }));
    }, []);
    const cardDirty = Object.values(dirtyByOutlet).some(Boolean);

    /** The outlet whose Google listing is being read (its slug), else null. */
    const [filling, setFilling] = useState<string | null>(null);
    /** The outlet asked to confirm before Google's listing replaces what is saved. */
    const [confirmFill, setConfirmFill] = useState<string | null>(null);
    const [fillRefused, setFillRefused] = useState<string | null>(null);
    /** Bumped per outlet when a fill lands, so its block remounts and the form shows the filled record. */
    const [filled, setFilled] = useState<Record<string, number>>({});

    useEffect(() => {
        if (cardDirty) setConfirmFill(null);
    }, [cardDirty]);

    // A different business is a different card: nothing carried over.
    useEffect(() => {
        // Any fill started for the earlier business is no longer this card's.
        activeFill.current += 1;
        setFilling(null);
        setConfirmFill(null);
        setFillRefused(null);
        setFilled({});
        setDirtyByOutlet({});
    }, [businessId]);

    /**
     * Fill one outlet from its public Google Maps listing (12 to 50 seconds).
     * The server writes only the fields a listing shows, over what was saved, and
     * keeps what staff alone know. It answers the whole payload like every other
     * write here, and the inputs on every outlet are off while it reads (`busy`):
     * an edit made in that time would be lost when the filled record lands.
     */
    const fillOutlet = async (slug: string, name: string) => {
        // Re-checked here, not only on the button: a confirmation stays open while
        // fields remain editable, so the form may have become dirty since it opened.
        if (cardDirty || filling !== null) {
            setConfirmFill(null);
            return;
        }
        const from = businessId;
        const mine = ++activeFill.current;
        const current = () => mine === activeFill.current && from === onScreen.current;
        setConfirmFill(null);
        setFillRefused(null);
        setFilling(slug);
        try {
            const r = await readGbpFromGoogle(from, slug);
            if (!current()) return;
            if (!r.ok) {
                // `gbp_google_unread`: no listing was clearly this outlet's. The server's sentence says why.
                setFillRefused(r.message);
                return;
            }
            snackbar.showSuccess("Filled from Google", outlets.length === 1 ? undefined : name);
            setFilled((was) => ({ ...was, [slug]: (was[slug] ?? 0) + 1 }));
            apply(r.data);
        } finally {
            // Only the fill that is still current gives the card back.
            if (current()) setFilling(null);
        }
    };
    const fillBlocked = cardDirty || filling !== null;
    const confirming = confirmFill !== null ? outlets.find((o) => o.outlet === confirmFill) ?? null : null;
    const fillingName = filling !== null ? outlets.find((o) => o.outlet === filling)?.name ?? null : null;

    return (
        <FoldCard
            id="gbp-profile"
            title="Google profile"
            icon={{ icon: MapPinIcon, area: "setup" }}
            foldedFact={load.kind === "ready" ? `${recorded} of ${outlets.length} recorded` : undefined}
        >
            <p className={cn(TYPE.tc, "m-0 pb-3 text-gray-500")}>
                What the client&rsquo;s Google Business Profile says now, and the before-screenshots. The GBP Pack is
                written from this.
            </p>
            {load.kind === "loading" && <p className={cn(TYPE.tb, "m-0 py-3 text-gray-500")}>Reading the Google profile&hellip;</p>}
            {load.kind === "failed" && <Refusal>{load.message}</Refusal>}
            {load.kind === "ready" && outlets.length === 0 && (
                <p className={cn(TYPE.tb, "m-0 py-3 text-gray-500")}>
                    This business has no outlets yet. They come from the plan.
                </p>
            )}
            {load.kind === "ready" && outlets.length > 0 && (
                // Outside the folds, so it shows with every outlet folded. One pill
                // per outlet; a business with one outlet gets the one pill.
                <div className="space-y-2 border-b border-gray-100 pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                        {outlets.length > 1 && <span className={cn(TYPE.tc, "text-gray-500")}>Fill from Google</span>}
                        {outlets.map((o) => (
                            <PillButton
                                key={o.outlet || "one"}
                                className="max-sm:h-9"
                                writes
                                busy={filling === o.outlet}
                                disabled={fillBlocked}
                                onClick={() => (recordedCount(load.data.fieldList, o).done > 0 ? setConfirmFill(o.outlet) : void fillOutlet(o.outlet, o.name))}
                            >
                                {outlets.length === 1 ? "Fill from Google" : o.name}
                            </PillButton>
                        ))}
                    </div>
                    {confirming && (
                        <>
                            <p className={cn(TYPE.tc, "m-0 text-gray-700")}>
                                This replaces what is saved for {confirming.name} in the fields Google shows. What only staff
                                know (whether the client manages it, products, posts, attributes, photos and notes) is kept.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                <PillButton writes variant="primary" className="max-sm:h-9" disabled={fillBlocked} onClick={() => void fillOutlet(confirming.outlet, confirming.name)}>
                                    Fill {confirming.name} from Google
                                </PillButton>
                                <PillButton className="max-sm:h-9" onClick={() => setConfirmFill(null)}>
                                    Cancel
                                </PillButton>
                            </div>
                        </>
                    )}
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                        {fillingName !== null
                            ? `Reading the Google Maps listing for ${fillingName}. This takes up to a minute.`
                            : cardDirty
                                ? "Save or undo your changes first: a fill would replace the form."
                                : "Reads the outlet’s public Google Maps listing."}
                    </p>
                    {fillRefused && <Refusal>{fillRefused}</Refusal>}
                </div>
            )}
            {load.kind === "ready" &&
                outlets.map((outlet) => (
                    <OutletBlock
                        // The slug keys the block, so switching business or
                        // reordering outlets starts a fresh form rather than
                        // leaving one outlet's draft on another's fields. A fill
                        // bumps its outlet's count: the block remounts, and the
                        // form shows the filled record.
                        key={`${businessId}:${outlet.outlet}:${filled[outlet.outlet] ?? 0}`}
                        businessId={businessId}
                        outlet={outlet}
                        fieldList={load.data.fieldList}
                        shotLabels={load.data.shotLabels}
                        onlyOne={outlets.length === 1}
                        onPayload={apply}
                        busy={filling !== null}
                        reportDirty={reportDirty}
                    />
                ))}
        </FoldCard>
    );
}

function OutletBlock({
    businessId,
    outlet,
    fieldList,
    shotLabels,
    onlyOne,
    onPayload,
    busy,
    reportDirty,
}: {
    businessId: string;
    outlet: GbpOutlet;
    fieldList: GbpField[];
    shotLabels: string[];
    onlyOne: boolean;
    onPayload: (data: GbpProfiles) => void;
    /** A Google fill is reading: no form on the card takes an edit meanwhile, so the answer cannot land on one. */
    busy: boolean;
    /** Tells the card whether this outlet's form has unsaved edits. */
    reportDirty: (outlet: string, dirty: boolean) => void;
}) {
    const snackbar = useSnackbar();
    const [draft, setDraft] = useState<GbpDraft>(() => draftFrom(fieldList, outlet));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [refused, setRefused] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // The stored record is the one the form was last saved against. A payload
    // that came back from somebody else's save on this outlet reloads the form
    // only where it has not been edited since.
    const saved = useMemo(() => draftFrom(fieldList, outlet), [fieldList, outlet]);
    const touched = useRef(false);
    useEffect(() => {
        if (!touched.current) setDraft(saved);
    }, [saved]);

    const set = (key: string, value: string) => {
        touched.current = true;
        setDraft((was) => ({ ...was, [key]: value }));
        setErrors((was) => (was[key] ? { ...was, [key]: "" } : was));
        setRefused(null);
    };

    const { done, total } = recordedCount(fieldList, outlet);
    const changed = draftChanged(fieldList, outlet, draft);

    const save = async () => {
        // What the server would refuse anyway, said without a round trip and
        // beside the field that caused it.
        const bad = checkDraft(fieldList, draft);
        setErrors(bad);
        setRefused(null);
        if (Object.keys(bad).length > 0) return;
        setSaving(true);
        const r = await saveGbpFields(businessId, outlet.outlet, fieldsFrom(fieldList, draft));
        setSaving(false);
        if (!r.ok) {
            // The refusal names the field's label; put it there when it does.
            const key = fieldErrorKey(fieldList, r.message);
            if (key) setErrors({ [key]: r.message });
            else setRefused(r.message);
            return;
        }
        touched.current = false;
        snackbar.showSuccess("Google profile saved", onlyOne ? undefined : outlet.name);
        onPayload(r.data);
    };

    // The card keeps one answer to "does any outlet have unsaved edits": a Google
    // fill answers the whole payload, so it must not start while any form is dirty.
    useEffect(() => {
        reportDirty(outlet.outlet, changed);
        return () => reportDirty(outlet.outlet, false);
    }, [changed, outlet.outlet, reportDirty]);

    return (
        <GroupFold
            id={`gbp-${outlet.outlet || "one"}`}
            tone={done > 0 ? "blue" : "gray"}
            title={outlet.name}
            count={done}
            lead="dot"
            defaultFolded={!onlyOne}
            note={`${done} of ${total} recorded`}
            wrap
        >
            <div className={cn(SURFACE.block, "space-y-4")}>
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    {outlet.updatedAt
                        ? `Last saved by ${outlet.updatedBy ?? "someone"} on ${dayDate(outlet.updatedAt) ?? outlet.updatedAt}.`
                        : "Not recorded yet."}
                    {" "}
                    {done === 0 ? "" : `${done} of ${total} fields on file.`}
                </p>
                {fieldList.map((field) => (
                    <GbpFieldInput
                        key={field.key}
                        field={field}
                        value={draft[field.key] ?? ""}
                        error={errors[field.key] || null}
                        disabled={saving || busy}
                        onChange={(v) => set(field.key, v)}
                    />
                ))}
                {refused && <Refusal>{refused}</Refusal>}
                <div className="flex flex-wrap items-center gap-2">
                    <PillButton writes variant="primary" className="max-sm:h-9" busy={saving} disabled={!changed} onClick={() => void save()}>
                        Save the profile
                    </PillButton>
                    {changed && !saving && (
                        <PillButton
                            className="max-sm:h-9"
                            onClick={() => { touched.current = false; setDraft(saved); setErrors({}); setRefused(null); }}
                        >
                            Undo my changes
                        </PillButton>
                    )}
                    {!changed && !saving && <span className={cn(TYPE.tc, "text-gray-500")}>Nothing to save.</span>}
                </div>
            </div>

            <Screenshots
                businessId={businessId}
                outlet={outlet}
                shotLabels={shotLabels}
                onPayload={onPayload}
            />
        </GroupFold>
    );
}

/** One field, drawn by its kind. The server's kinds are the only ones there are. */
function GbpFieldInput({
    field,
    value,
    error,
    disabled,
    onChange,
}: {
    field: GbpField;
    value: string;
    error: string | null;
    disabled: boolean;
    onChange: (v: string) => void;
}) {
    const counter = counterText(field, value);
    // A text field with a big limit is a paragraph; a short one is a line.
    const asTextarea = field.kind === "list" || (field.kind === "text" && (field.limit ?? 0) > 300);

    if (field.kind === "bool") {
        return (
            <fieldset className="min-w-0">
                <legend className={cn(TYPE.lab, "mb-1.5 block")}>{field.label}</legend>
                <div className="flex flex-wrap items-center gap-2">
                    {[
                        { v: BOOL_YES, label: "Yes" },
                        { v: BOOL_NO, label: "No" },
                        // Not recorded is a real third answer, and the one
                        // every field starts in: it must be reachable again
                        // after a yes or a no was picked by mistake.
                        { v: "", label: "Not recorded" },
                    ].map((choice) => (
                        <label
                            key={choice.v || "unset"}
                            className={cn(
                                TYPE.tc,
                                "flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1 font-medium",
                                value === choice.v ? "bg-blue-600 text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                            )}
                        >
                            <input
                                type="radio"
                                className="sr-only"
                                name={`gbp-${field.key}`}
                                checked={value === choice.v}
                                disabled={disabled}
                                onChange={() => onChange(choice.v)}
                            />
                            {choice.label}
                        </label>
                    ))}
                </div>
                {error && <p role="alert" className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>{error}</p>}
            </fieldset>
        );
    }

    return (
        <Field
            label={field.label}
            error={error}
            hint={error ? undefined : (counter ?? (field.kind === "list" ? "One per line." : undefined)) ?? undefined}
        >
            {({ id, describedBy }) =>
                asTextarea ? (
                    <textarea
                        id={id}
                        aria-describedby={describedBy}
                        value={value}
                        rows={field.kind === "list" ? 4 : 5}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                        className={cn(textareaClass(error), "min-h-24")}
                    />
                ) : (
                    <input
                        id={id}
                        aria-describedby={describedBy}
                        // `inputMode`, not `type="number"`: a number input
                        // silently drops what it cannot parse, so a mistyped
                        // rating would vanish instead of being told about.
                        inputMode={field.kind === "number" ? "decimal" : undefined}
                        value={value}
                        maxLength={field.kind === "text" ? (field.limit ?? undefined) : undefined}
                        autoComplete="off"
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                        className={inputClass(error)}
                    />
                )
            }
        </Field>
    );
}

/** The before-screenshots for one outlet: add one per section, remove a wrong one. */
function Screenshots({
    businessId,
    outlet,
    shotLabels,
    onPayload,
}: {
    businessId: string;
    outlet: GbpOutlet;
    shotLabels: string[];
    onPayload: (data: GbpProfiles) => void;
}) {
    const snackbar = useSnackbar();
    const [label, setLabel] = useState(shotLabels[0] ?? "other");
    const [caption, setCaption] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const writesPaused = useWritesPaused();

    const full = outlet.screenshots.length >= GBP_MAX_SHOTS;

    const pick = (f: File | undefined) => {
        setRefused(null);
        if (!f) { setFile(null); return; }
        // The server's own two rules, said before the file goes over the wire.
        if (!GBP_SHOT_TYPES.includes(f.type)) { setRefused("A screenshot is a PNG, JPG or WebP image."); setFile(null); return; }
        if (f.size > GBP_SHOT_MAX_BYTES) { setRefused("A screenshot is 20 MB or less."); setFile(null); return; }
        setFile(f);
    };

    const after = (r: GbpResult, done: string) => {
        if (!r.ok) { setRefused(r.message); return false; }
        snackbar.showSuccess(done);
        onPayload(r.data);
        return true;
    };

    const add = async () => {
        if (!file) return;
        setBusy(true);
        setRefused(null);
        const r = await addGbpScreenshot(businessId, outlet.outlet, { file, label, caption });
        setBusy(false);
        if (after(r, "Screenshot added")) {
            setFile(null);
            setCaption("");
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const remove = async (shot: GbpShot) => {
        setRemoving(shot.id);
        setRefused(null);
        const r = await removeGbpScreenshot(businessId, outlet.outlet, shot.id);
        setRemoving(null);
        after(r, "Screenshot removed");
    };

    return (
        <div className={cn(SURFACE.block, "mt-3 space-y-3")}>
            <h4 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>
                Before-screenshots{" "}
                <span className="font-normal text-gray-500">
                    {outlet.screenshots.length} of {GBP_MAX_SHOTS}
                </span>
            </h4>

            {outlet.screenshots.length > 0 && (
                <ul className="list-plain m-0 grid grid-cols-2 gap-3 p-0 sm:grid-cols-3">
                    {outlet.screenshots.map((shot) => (
                        <li key={shot.id} className="min-w-0 rounded-xl bg-white ring-1 ring-inset ring-gray-200">
                            {/* The link, not the image, is the control: the URL
                                is presigned for an hour, so a thumbnail that
                                has expired still has a row that says what it
                                is. */}
                            <a
                                href={shot.url ?? undefined}
                                target="_blank"
                                rel="noreferrer"
                                className={cn("block aspect-video overflow-hidden rounded-t-xl bg-gray-50", FOCUS_RING)}
                            >
                                {shot.url ? (
                                    <img src={shot.url} alt={shot.caption || shotLabelText(shot.label)} className="h-full w-full object-cover" />
                                ) : (
                                    <span className="flex h-full w-full items-center justify-center text-gray-400">
                                        <ImageIcon aria-hidden size={18} />
                                    </span>
                                )}
                            </a>
                            <div className="flex items-start gap-2 p-2">
                                <span className="min-w-0 flex-1">
                                    <span className={cn(TYPE.tc, "block font-medium text-gray-900")}>{shotLabelText(shot.label)}</span>
                                    {shot.caption && <span className={cn(TYPE.tc, "block text-gray-500")}>{shot.caption}</span>}
                                    <span className={cn(TYPE.tc, "block text-gray-400")}>
                                        {[shot.addedBy, dayDate(shot.addedAt)].filter(Boolean).join(" · ")}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    aria-label={`Remove the ${shotLabelText(shot.label)} screenshot`}
                                    disabled={removing !== null || writesPaused}
                                    onClick={() => void remove(shot)}
                                    className={cn("shrink-0 rounded p-1 text-gray-400 hover:text-rose-600 disabled:opacity-50", FOCUS_RING)}
                                >
                                    {removing === shot.id
                                        ? <Loader2Icon aria-hidden size={14} className="animate-spin" />
                                        : <TrashIcon aria-hidden size={14} />}
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {full ? (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    At most {GBP_MAX_SHOTS} screenshots per outlet. Remove one to add another.
                </p>
            ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Field label="Section">
                        {({ id }) => (
                            <select id={id} className={inputClass()} value={label} disabled={busy} onChange={(e) => setLabel(e.target.value)}>
                                {shotLabels.map((l) => (
                                    <option key={l} value={l}>{shotLabelText(l)}</option>
                                ))}
                            </select>
                        )}
                    </Field>
                    <Field label="Caption" hint="Optional.">
                        {({ id }) => (
                            <input
                                id={id}
                                value={caption}
                                maxLength={GBP_CAPTION_MAX}
                                autoComplete="off"
                                disabled={busy}
                                onChange={(e) => setCaption(e.target.value)}
                                className={inputClass()}
                            />
                        )}
                    </Field>
                    <Field label="Image" hint="PNG, JPG or WebP.">
                        {({ id }) => (
                            <input
                                id={id}
                                ref={fileRef}
                                type="file"
                                accept={GBP_SHOT_TYPES.join(",")}
                                disabled={busy || writesPaused}
                                onChange={(e) => pick(e.target.files?.[0])}
                                className={cn(TYPE.tc, "block w-full text-gray-500")}
                            />
                        )}
                    </Field>
                </div>
            )}

            {refused && <Refusal>{refused}</Refusal>}

            {!full && (
                <PillButton writes className="max-sm:h-9" busy={busy} disabled={!file} onClick={() => void add()}>
                    <UploadIcon aria-hidden size={14} />
                    Add the screenshot
                </PillButton>
            )}
        </div>
    );
}
